import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { combineLatest } from 'rxjs';

import { IconComponent } from '@shared/components/icon/icon.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

import {
  TICPCChartLabel,
  TICPCChartType,
  TrialInsightsClinicalPatientCostChartService,
} from './patient-cost-chart.service';
import { TrialInsightsClinicalPatientCostQueryService } from './patient-cost-query.service';
import { TrialInsightsClinicalPatientCostStoreService } from './patient-cost-store.service';
import { TrialInsightsClinicalPatientCostTableService } from './patient-cost-table.service';
import { GenericTrialInsightsComponent } from '../../classes/trial-insights-component.class';
import { TrialInsightsHeaderComponent } from '../trial-insights-header/trial-insights-header.component';
import { TrialInsightsLegendComponent } from '../trial-insights-legend/trial-insights-legend.component';
import { TrialInsightsTableComponent } from '../trial-insights-table/trial-insights-table.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TrialInsightsHeaderComponent,
    IconComponent,
    NgStyle,
    TooltipDirective,
    NgChartsModule,
    TrialInsightsLegendComponent,
    TrialInsightsTableComponent,
  ],
  providers: [TrialInsightsClinicalPatientCostQueryService],
  selector: 'aux-trial-insights-clinical-patient-cost',
  styleUrls: ['../../trial-insights.component.scss'],
  templateUrl: './patient-cost.component.html',
})
export class TrialInsightsClinicalPatientCostComponent extends GenericTrialInsightsComponent<
  TICPCChartType,
  TICPCChartLabel
> {
  storeService = inject(TrialInsightsClinicalPatientCostStoreService);

  cdr: ChangeDetectorRef;
  chartService: TrialInsightsClinicalPatientCostChartService;
  queryService: TrialInsightsClinicalPatientCostQueryService;
  tableService: TrialInsightsClinicalPatientCostTableService;

  constructor() {
    const chartService = inject(TrialInsightsClinicalPatientCostChartService);
    const tableService = inject(TrialInsightsClinicalPatientCostTableService);
    const queryService = inject(TrialInsightsClinicalPatientCostQueryService);
    const cdr = inject(ChangeDetectorRef);

    super({
      cdr,
      chartService,
      color: '#094673',
      exceedMessage: 'Patients Enrolled (actual) has exceeded Patients Enrolled (expected)',
      queryService,
      tableService,
      title: 'Average Incurred Patient Cost (Total)',
    });

    this.chartService = chartService;
    this.tableService = tableService;
    this.queryService = queryService;
    this.cdr = cdr;
  }

  // Prevent chart from redrawing when
  // data is identical
  setChartData(chartOptions: ChartConfiguration<'bar'>) {
    const dataset = this.chartOptions.data.datasets;
    const nextDataset = chartOptions.data.datasets;

    if (!dataset.length || !nextDataset.length) {
      this.chartOptions = chartOptions;

      return;
    }

    const data = dataset[0].data;
    const nextData = nextDataset[0].data;

    if (!data?.length || !nextData?.length) {
      this.chartOptions = chartOptions;

      return;
    }

    const value = data[0];
    const nextValue = nextData[0];

    if (value !== nextValue) {
      this.chartOptions = chartOptions;

      return;
    }
  }

  subscribeToData = () => {
    this.queryService
      .processPatientCostSummary$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.storeService
      .getPatientCostSummary$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  };

  subscribeToOptions = () => {
    combineLatest([
      this.queryService.chartOptions,
      this.queryService.legendOptions,
      this.queryService.tableOptions,
      this.queryService.totalAmount,
      this.queryService.expectedEnrolled,
      this.queryService.expectedEnrolledExceeded,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([
          chartOptions,
          legendOptions,
          tableOptions,
          totalAmount,
          expectedEnrolled,
          expectedEnrolledExceeded,
        ]) => {
          this.setChartData(chartOptions);
          this.legendOptions = legendOptions;
          this.tableOptions = tableOptions;
          this.total = totalAmount;
          this.expectedEnrolled = expectedEnrolled;
          this.expectedEnrolledExceeded = expectedEnrolledExceeded;

          this.cdr.markForCheck();
        }
      );
  };
}
